
import React, { useState, useEffect, useCallback, useRef } from "react";
import { safeFormat } from '@/lib/safeDate';
import { Lead } from "@/api/entities";
import { User } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit, Mail, Phone, Building, MoreVertical, Globe, Calendar, MapPin, UserCog, Loader2, Languages, Users, Copy, FileText, AlertCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGoBack } from "@/hooks/useGoBack";
import { createPageUrl } from "@/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns"; // Import format from date-fns
import { getStateName, getCountryName } from "@/lib/locationUtils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import LeadForm from "../components/leads/LeadForm";
import TasksTab from "../components/leads/TasksTab";
import CallsTab from "../components/contacts/CallsTab";
import { ChatCore, ContactChatHeader } from "@/features/chat";
import DocumentsTab from "../components/leads/DocumentsTab";
import { MeetingsTab } from "../components/deals/MeetingsTab";
import NotesTab from "../components/contacts/NotesTab";
import { useTwilio } from "@/contexts/TwilioContext";
import { getAvatarColor, formatPhoneNumber, normalizePhone } from "@/lib/utils";
import { useEligibilityPdf } from "@/hooks/useEligibilityPdf";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSalesUsers } from "@/hooks/useSalesUsers";
import { useAppUsers } from "@/hooks/useAppUsers";

const statusColors = {
  new: "bg-slate-100 text-slate-700 border-slate-200",
  contacted: "bg-purple-100 text-purple-700 border-purple-200",
  qualifying: "bg-amber-100 text-amber-700 border-amber-200",
  qualified: "bg-emerald-100 text-emerald-700 border-emerald-200",
  converted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  lost: "bg-slate-100 text-slate-700 border-slate-200"
};

const sourceColors = {
  website: "bg-slate-100 text-slate-700",
  referral: "bg-purple-100 text-purple-700",
  social_media: "bg-pink-100 text-pink-700",
  email_campaign: "bg-indigo-100 text-indigo-700",
  phone_call: "bg-emerald-100 text-emerald-700",
  walk_in: "bg-amber-100 text-amber-700",
  "Facebook Lead Ads": "bg-indigo-100 text-indigo-700",
  facebook_lead_ads: "bg-indigo-100 text-indigo-700",
  other: "bg-slate-100 text-slate-700"
};

export default function LeadDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useGoBack('/leads');
  const [lead, setLead] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [user, setUser] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showNotConvertDialog, setShowNotConvertDialog] = useState(false);
  const [notConvertedReason, setNotConvertedReason] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [tasksCount, setTasksCount] = useState(0);
  const [callsCount, setCallsCount] = useState(0);
  const [messagesCount, setMessagesCount] = useState(0);
  const [meetingsCount, setMeetingsCount] = useState(0);
  const [notesCount, setNotesCount] = useState(0);
  const { makeCall, isReady, isInitiatingCall } = useTwilio();
  
  const urlParams = new URLSearchParams(window.location.search);
  const leadId = urlParams.get('id');
  const { hasPdf, pdfLinked, loading: pdfLoading, isLinking, linkPdf, refresh: refreshPdf } = useEligibilityPdf(leadId || '');
  const isConvertingRef = useRef(false); // Prevent double-click on convert
  const { salesUsers, isLoading: isLoadingSalesUsers } = useSalesUsers();
  const { users, loading: usersLoading } = useAppUsers();

  const notConvertedReasons = [
    'Fear/Insecurity',
    'Ghosted/No Response',
    'Indirect Inquiry',
    'Invalid Lead',
    'Needs Approval',
    'No Budget',
    'No Urgency',
    'Not Eligible',
    'Seasonal/Timing',
    'Went with Competitor'
  ];

  const loadLeadData = useCallback(async () => {
    setIsLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    const leadId = urlParams.get('id');

    if (!leadId) {
      navigate(createPageUrl("Leads"));
      return;
    }

    try {
      // Fetch lead from Supabase
      const { data: leadData, error: leadError } = await supabase
        .from('leads')
        .select('*')
        .eq('id', leadId)
        .maybeSingle();

      if (leadError) {
        console.error("Error loading lead:", leadError);
        toast.error("Error loading lead details");
        navigate(createPageUrl("Leads"));
        return;
      }

      if (!leadData) {
        toast.error("Lead not found");
        navigate(createPageUrl("Leads"));
        return;
      }

      // Map Supabase data to Lead format
      const mappedLead = {
        id: leadData.id,
        first_name: leadData.first_name || 'Lead',
        last_name: leadData.last_name || '',
        email: leadData.primary_email,
        phone: leadData.primary_phone_country_code && leadData.primary_phone 
          ? `${leadData.primary_phone_country_code}${leadData.primary_phone}` 
          : leadData.primary_phone,
        primary_phone_country_code: leadData.primary_phone_country_code,
        company: null,
        status: leadData.lifecycle_stage || 'new',
        source: leadData.origem || 'unknown',
        assigned_to: leadData.assigned_to,
        created_date: leadData.created_at,
        updated_date: leadData.updated_at,
        not_converted_reason: leadData.not_converted_reason,
        date_of_birth: leadData.date_of_birth,
        gender: leadData.gender,
        country_of_birth: leadData.country_of_birth,
        language: leadData.language,
        address: {
          street: leadData.primary_address_line1 || '',
          line2: leadData.primary_address_line2 || '',
          city: leadData.primary_address_city || '',
          state: leadData.primary_address_state || '',
          zip: leadData.primary_address_postal_code || '',
          country: leadData.primary_address_country || ''
        }
      };

      setLead(mappedLead);
    } catch (error) {
      console.error("Error loading lead data:", error);
    }

    setIsLoading(false);
  }, [navigate]);

  // Separate effect to update assigned user when salesUsers or lead changes
  useEffect(() => {
    if (lead?.assigned_to && salesUsers.length > 0) {
      const foundUser = salesUsers.find(u => u.email === lead.assigned_to || u.id === lead.assigned_to);
      setAssignedUser(foundUser || null);
    } else {
      setAssignedUser(null);
    }
  }, [lead, salesUsers]);

  useEffect(() => {
    loadUser();
    loadLeadData();
  }, [loadLeadData]);

  // Carrega contadores
  useEffect(() => {
    const loadCounts = async () => {
      if (!leadId) return;

      // Conta tasks
      const tasksArray = lead?.tasks || [];
      setTasksCount(Array.isArray(tasksArray) ? tasksArray.length : 0);

      // Conta calls
      const { count: callsTotal } = await supabase
        .from('calls')
        .select('*', { count: 'exact', head: true })
        .eq('lead_id', leadId);
      setCallsCount(callsTotal || 0);

      // Count WhatsApp messages
      const { count: messagesTotal } = await supabase
        .from('whatsapp_messages')
        .select('*', { count: 'exact', head: true })
        .eq('lead_id', leadId);
      setMessagesCount(messagesTotal || 0);

      // Count meetings
      const { count: meetingsTotal } = await supabase
        .from('calls')
        .select('*', { count: 'exact', head: true })
        .eq('call_type', 'meeting')
        .eq('lead_id', leadId);
      setMeetingsCount(meetingsTotal || 0);

      // Count notes
      const { count: notesTotal } = await supabase
        .from('notes')
        .select('*', { count: 'exact', head: true })
        .eq('lead_id', leadId);
      setNotesCount(notesTotal || 0);
    };

    loadCounts();
  }, [leadId, lead?.tasks]);

  // Pre-seleciona usuário logado quando dialog de conversão abre
  useEffect(() => {
    if (showConvertDialog && !selectedUserId) {
      const preSelectCurrentUser = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: appUser } = await supabase
            .from('app_users')
            .select('id')
            .eq('auth_user_id', user.id)
            .single();
          if (appUser) {
            setSelectedUserId(appUser.id);
          }
        }
      };
      preSelectCurrentUser();
    }
  }, [showConvertDialog]);

  const loadUser = async () => {
    try {
      const userData = await User.me();
      setUser(userData);
    } catch (error) {
      console.error("Error loading user:", error);
    }
  };

  const handleUpdateLead = async (leadData) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('leads')
        .update({
          first_name: leadData.first_name,
          last_name: leadData.last_name,
          preferred_name: leadData.preferred_name || null,
          primary_phone: leadData.primary_phone,
          primary_email: leadData.primary_email,
          origem: leadData.origem,
          market: leadData.market,
          date_of_birth: leadData.date_of_birth || null,
          gender: leadData.gender || null,
          language: leadData.language || null,
          country_of_birth: leadData.country_of_birth || null,
          primary_address_line1: leadData.address_line1 || null,
          primary_address_line2: leadData.address_line2 || null,
          primary_address_city: leadData.city || null,
          primary_address_state: leadData.state || null,
          primary_address_postal_code: leadData.postal_code || null,
          primary_address_country: leadData.country || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', lead.id);
      
      if (error) throw error;
      
      toast.success('Lead updated successfully');
      setIsEditing(false);
      loadLeadData();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error('Error updating lead');
    }
    setIsProcessing(false);
  };

  const handleUpdateTasks = async (tasks) => {
    setIsProcessing(true);
    try {
      await Lead.update(lead.id, { tasks });
      await loadLeadData();
    } catch (error) {
      console.error("Error updating tasks:", error);
    }
    setIsProcessing(false);
  };

  const handleCallLead = async () => {
    if (!lead?.phone || !isReady) {
      toast.error('Unable to start call');
      return;
    }
    
    try {
      if (window.innerWidth >= 768) {
        toast.info('Starting call...');
      }
      await makeCall(lead.phone, lead.contact_id || undefined, lead.id);
    } catch (error) {
      console.error("Error initiating call:", error);
      toast.error('Error starting call');
    }
  };

  const handleConvertLead = async () => {
    // Prevent double-click
    if (isConvertingRef.current) {
      console.log('⚠️ Conversion already in progress, ignoring click');
      return;
    }

    if (!selectedUserId) {
      toast.error('Please select a user for the deal');
      return;
    }

    isConvertingRef.current = true;
    setShowConvertDialog(false);
    setIsProcessing(true);
    try {
      // Busca dados completos do lead
      const { data: fullLeadData, error: fetchError } = await supabase
        .from('leads')
        .select('*')
        .eq('id', lead.id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // ✅ FASE 5: Verificar se lead JÁ tem contact_id (conversão inteligente)
      let contactToUse;
      let existingContact = null; // ✅ Declarar ANTES do if para estar no escopo correto

      console.log('🔍 Lead conversion - checking contact:', { 
        leadId: fullLeadData.id, 
        hasContactId: !!fullLeadData.contact_id,
        phone: fullLeadData.primary_phone,
        email: fullLeadData.primary_email
      });

      if (fullLeadData.contact_id) {
        // ✅ Lead já vinculado a contato existente - PULAR CRIAÇÃO
        console.log('✅ Lead already has contact_id:', fullLeadData.contact_id);
        
        const { data: linkedContact, error: fetchError } = await supabase
          .from('contacts')
          .select('*')
          .eq('id', fullLeadData.contact_id)
          .single();
        
        if (fetchError) {
          throw new Error('Erro ao buscar contato vinculado');
        }
        
        existingContact = linkedContact;
        contactToUse = linkedContact;
        toast.info(`Usando contato existente: ${contactToUse.first_name} ${contactToUse.last_name || ''}`);
        
      } else {
        // Lead não tem contato - verificar duplicata via RPC e criar se necessário
        console.log('🔍 Lead has no contact_id, checking for duplicates...');
        
        if (fullLeadData.primary_phone && fullLeadData.primary_phone.trim()) {
          // ✅ Usar RPC confiável em vez de lógica manual
          const { data: foundContact, error: rpcError } = await supabase
            .rpc('check_duplicate_contact_phone', {
              p_phone: fullLeadData.primary_phone,
              p_country_code: fullLeadData.primary_phone_country_code || null,
              p_exclude_id: null
            })
            .maybeSingle();
          
          if (rpcError) {
            console.error('❌ Error checking duplicate contact:', rpcError);
          } else if (foundContact) {
            console.log('✅ Found existing contact via phone:', foundContact.id);
            existingContact = foundContact;
            
            // Vincular lead ao contato existente
            const { error: linkError } = await supabase
              .from('leads')
              .update({ contact_id: foundContact.id })
              .eq('id', leadId);
            
            if (linkError) {
              console.error('❌ Error linking lead to contact:', linkError);
            } else {
              console.log('✅ Lead linked to existing contact');
            }
          } else {
            console.log('ℹ️ No duplicate contact found via phone');
          }
        }
        
        // Usa contato existente ou cria novo
        if (existingContact) {
          console.log('✅ Using existing contact:', existingContact.id);
          contactToUse = existingContact;
          toast.info(`Contato já existe. Criando deal para ${contactToUse.first_name} ${contactToUse.last_name || ''}`);
        } else {
          // Cria novo contato
          console.log('➕ Creating new contact...');
          const { data: newContact, error: contactError } = await supabase
            .from('contacts')
            .insert({
              first_name: fullLeadData.first_name,
              last_name: fullLeadData.last_name,
              preferred_name: fullLeadData.preferred_name,
              primary_email: fullLeadData.primary_email,
              primary_phone: fullLeadData.primary_phone,
              primary_phone_country_code: fullLeadData.primary_phone_country_code,
              gender: fullLeadData.gender,
              language: fullLeadData.language,
              date_of_birth: fullLeadData.date_of_birth,
              country_of_birth: fullLeadData.country_code,
              status: 'active',
            })
            .select()
            .single();
          
          if (contactError) throw contactError;
          console.log('✅ New contact created:', newContact.id);
          contactToUse = newContact;
        }
      }

      // Cria endereço apenas se for um contato novo e houver dados
      const hasAddressData = fullLeadData.primary_address_line1 || fullLeadData.primary_address_city;
      if (!existingContact && hasAddressData) {
        const { error: addressError } = await supabase
          .from('contact_addresses')
          .insert({
            contact_id: contactToUse.id,
            type: 'other',
            line1: fullLeadData.primary_address_line1 || null,
            line2: fullLeadData.primary_address_line2 || null,
            city: fullLeadData.primary_address_city || null,
            state: fullLeadData.primary_address_state || null,
            postal_code: fullLeadData.primary_address_postal_code || null,
            country_code: fullLeadData.primary_address_country ? fullLeadData.primary_address_country.substring(0, 2) : null,
            is_primary: true
          });
        
        if (addressError) console.error("Error creating address:", addressError);
      }

      // Busca o usuário logado (quem está convertendo)
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { data: currentAppUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', authUser.id)
        .single();

      // Atribuição de Consultor não vive mais no contato — quem converte vira
      // sales_consultant do deal logo abaixo, na criação do deal.
      void currentAppUser;

      // ✅ VERIFICAR SE JÁ EXISTE DEAL ATIVO PARA ESTE CONTATO
      const { data: existingDeal, error: existingDealError } = await supabase
        .from('deals')
        .select('id, name, stage')
        .eq('contact_id', contactToUse.id)
        .not('stage', 'in', '("won","lost")')
        .maybeSingle();

      if (existingDealError) {
        console.error("Error checking existing deal:", existingDealError);
      }

      if (existingDeal) {
        toast.info(`Este contato já possui um Deal ativo (${existingDeal.stage}). Redirecionando...`);
        
        // Atualiza o lead marcando como qualified (sem criar novo deal)
        await supabase.rpc('convert_lead_to_contact', {
          p_lead_id: lead.id,
          p_contact_id: contactToUse.id
        });
        
        setSelectedUserId(null);
        isConvertingRef.current = false;
        setIsProcessing(false);
        navigate(`/deal-detail?id=${existingDeal.id}`);
        return;
      }

      // Cria o deal com o usuário selecionado
      const contactName = `${contactToUse.first_name || ''} ${contactToUse.last_name || ''}`.trim();
      const { data: newDeal, error: dealError } = await supabase
        .from('deals')
        .insert({
          contact_id: contactToUse.id,
          sales_consultant: selectedUserId,  // Vendedor responsável (selecionado no dialog)
          created_by: currentAppUser?.id,   // Quem está convertendo (usuário logado)
          source: 'pipeline',
          stage: 'NEW',
          total_amount: 0,
          currency: 'USD',
          name: contactName || 'New Deal',
          notes: `Converted from lead: ${fullLeadData.first_name} ${fullLeadData.last_name}`
        })
        .select()
        .single();
      
      if (dealError) throw dealError;
      
      // Migrate meetings from lead to deal
      const { error: meetingsError } = await supabase
        .from('calls')
        .update({ 
          deal_id: newDeal.id,
          lead_id: null
        })
        .eq('call_type', 'meeting')
        .eq('lead_id', lead.id);
      
      if (meetingsError) console.error("Error migrating meetings:", meetingsError);

      // Migrate notes from lead to both contact and deal
      const { data: leadNotes, error: fetchNotesError } = await supabase
        .from('notes')
        .select('*')
        .eq('lead_id', lead.id);

      if (!fetchNotesError && leadNotes && leadNotes.length > 0) {
        // Para cada nota, criar uma cópia para o contact e uma para o deal
        for (const note of leadNotes) {
          // Nota para o contact
          await supabase
            .from('notes')
            .insert({
              contact_id: contactToUse.id,
              title: note.title,
              content: note.content,
              created_by: note.created_by,
              created_at: note.created_at,
            });
          
          // Nota para o deal
          await supabase
            .from('notes')
            .insert({
              deal_id: newDeal.id,
              title: note.title,
              content: note.content,
              created_by: note.created_by,
              created_at: note.created_at,
            });
        }
        
        console.log(`Migrated ${leadNotes.length} notes to contact and deal`);
      }
      
      // Atualiza o lead com contact_id e marca como convertido usando RPC (bypassa RLS)
      const { error: updateError } = await supabase.rpc('convert_lead_to_contact', {
        p_lead_id: lead.id,
        p_contact_id: contactToUse.id
      });
      
      if (updateError) throw updateError;
      
      // ✅ Mensagem apropriada baseada no cenário
      if (fullLeadData.contact_id) {
        toast.success(`Lead convertido! Deal criado para contato já existente.`);
      } else if (contactToUse.id !== newContact?.id) {
        toast.success(`Lead convertido! Deal criado para contato existente.`);
      } else {
        toast.success(`Lead convertido com sucesso! Contato e Deal criados.`);
      }
      setSelectedUserId(null);
      isConvertingRef.current = false;
      await loadLeadData();
    } catch (error) {
      console.error("Error converting lead:", error);
      toast.error('Error converting lead: ' + (error.message || 'Unknown error'));
    }
    isConvertingRef.current = false;
    setIsProcessing(false);
  };

  const handleNotConvertLead = async () => {
    if (!notConvertedReason) {
      toast.error('Please select a reason');
      return;
    }
    
    setShowNotConvertDialog(false);
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('leads')
        .update({
          lifecycle_stage: 'not_qualified',
          not_converted_reason: notConvertedReason,
          updated_at: new Date().toISOString()
        })
        .eq('id', lead.id);
      
      if (error) throw error;
      
      toast.success('Lead marked as not converted');
      setNotConvertedReason('');
      await loadLeadData();
    } catch (error) {
      console.error("Error marking lead as not converted:", error);
      toast.error('Error updating lead');
    }
    setIsProcessing(false);
  };

  const handleCloneLead = async () => {
    setIsProcessing(true);
    try {
      // Fetch the original lead
      const { data: originalLead, error: fetchErr } = await supabase
        .from('leads')
        .select('*')
        .eq('id', lead.id)
        .single();

      if (fetchErr) throw fetchErr;

      // Create new lead with same data but reset status
      const { id: _id, created_at: _created, updated_at: _updated, contact_id: _contact, converted_at: _converted, lifecycle_stage: _stage, not_converted_reason: _reason, ...leadData } = originalLead;
      
      const { data: newLead, error: err } = await supabase
        .from('leads')
        .insert([{
          ...leadData,
          lifecycle_stage: 'new',
        }])
        .select()
        .single();

      if (err) throw err;

      toast.success('Lead cloned successfully');
      navigate(createPageUrl("LeadDetail", `id=${newLead.id}`));
    } catch (error) {
      console.error("Error cloning lead:", error);
      toast.error('Error cloning lead');
    }
    setIsProcessing(false);
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Lead not found or you don't have permission to view it.
            </AlertDescription>
          </Alert>
          <Button 
            onClick={() => navigate(createPageUrl("Leads"))}
            className="mt-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Leads
          </Button>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 p-6 md:p-8">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="outline"
            onClick={() => setIsEditing(false)}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <LeadForm
            lead={lead}
            onSubmit={handleUpdateLead}
            onCancel={() => setIsEditing(false)}
            isProcessing={isProcessing}
          />
        </div>
      </div>
    );
  }

  const fullName = `${lead.first_name} ${lead.last_name}`;
  const initials = `${lead.first_name.charAt(0)}${lead.last_name.charAt(0)}`.toUpperCase();

  return (
    <div className="bg-white dark:bg-slate-900 p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={goBack}
          className="mb-6 -ml-2 text-slate-600 dark:text-slate-400 flex items-center gap-2 hover:scale-105 transition-transform duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 bg-gradient-to-br ${getAvatarColor(lead?.first_name)} rounded-full flex items-center justify-center shadow-lg`}>
              <span className="text-white font-bold text-2xl">{initials}</span>
            </div>
              <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{fullName}</h1>
                {['new', 'contacted', 'qualifying'].includes(lead.status) && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowConvertDialog(true)}
                      disabled={isProcessing}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Convert
                    </Button>
                    <Button
                      onClick={() => setShowNotConvertDialog(true)}
                      disabled={isProcessing}
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      Not Convert
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                <Badge className={`${statusColors[lead.status]} border font-medium pointer-events-none`}>
                  {lead.status}
                </Badge>
                {lead.source && (
                  <Badge className={`${sourceColors[lead.source] || 'bg-slate-100 text-slate-700'} border-0 font-medium pointer-events-none`}>
                    {lead.source.replace(/_/g, ' ')}
                  </Badge>
                )}
                {lead.status === 'disqualified' && lead.not_converted_reason && (
                  <Badge className="bg-red-50 text-red-700 border-red-200 border font-medium pointer-events-none">
                    {lead.not_converted_reason}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-slate-600 hover:text-slate-600 hover:bg-transparent transition-all duration-200 hover:scale-105"
              >
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {['qualified', 'not_qualified'].includes(lead.status) ? (
                <DropdownMenuItem onClick={handleCloneLead} disabled={isProcessing}>
                  <Copy className="w-4 h-4 mr-2" />
                  Clone Lead
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Lead
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div>
          <div className="flex gap-2 mb-8 bg-slate-100 dark:bg-slate-800 p-1.5 rounded-full inline-flex overflow-x-auto">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full whitespace-nowrap ${
                activeTab === "overview"
                  ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("tasks")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full flex items-center gap-2 whitespace-nowrap ${
                activeTab === "tasks"
                  ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              }`}
            >
              Tasks
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeTab === "tasks" 
                  ? "bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200" 
                  : "bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300"
              }`}>
                {tasksCount}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("calls")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full flex items-center gap-2 whitespace-nowrap ${
                activeTab === "calls"
                  ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              }`}
            >
              Calls
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeTab === "calls" 
                  ? "bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200" 
                  : "bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300"
              }`}>
                {callsCount}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("meetings")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full flex items-center gap-2 whitespace-nowrap ${
                activeTab === "meetings"
                  ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              }`}
            >
              Meetings
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeTab === "meetings" 
                  ? "bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200" 
                  : "bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300"
              }`}>
                {meetingsCount}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("whatsapp")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full flex items-center gap-2 whitespace-nowrap ${
                activeTab === "whatsapp"
                  ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              }`}
            >
              Messages
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeTab === "whatsapp" 
                  ? "bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200" 
                  : "bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300"
              }`}>
                {messagesCount}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("documents")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full flex items-center gap-2 whitespace-nowrap ${
                activeTab === "documents"
                  ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              }`}
            >
              Documentos
              {hasPdf && !pdfLoading && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === "documents" 
                    ? "bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-200" 
                    : "bg-blue-200 dark:bg-blue-800 text-blue-600 dark:text-blue-300"
                }`}>
                  {pdfLinked ? '✓' : '!'}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("notes")}
              className={`px-6 py-2.5 font-medium transition-all rounded-full flex items-center gap-2 whitespace-nowrap ${
                activeTab === "notes"
                  ? "text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              }`}
            >
              Notes
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeTab === "notes" 
                  ? "bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200" 
                  : "bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300"
              }`}>
                {notesCount}
              </span>
            </button>
          </div>

          {activeTab === "overview" && (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                {/* Alerta de PDF não vinculado */}
                {hasPdf && !pdfLinked && !pdfLoading && (
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="flex items-center justify-between">
                      <span className="text-amber-800">
                        Este lead possui um PDF de avaliação que não está vinculado aos documentos.
                      </span>
                      <Button
                        onClick={async () => {
                          try {
                            await linkPdf();
                            toast.success('PDF vinculado com sucesso!');
                            await refreshPdf();
                          } catch (error) {
                            toast.error('Erro ao vincular PDF');
                            console.error(error);
                          }
                        }}
                        disabled={isLinking}
                        size="sm"
                        className="ml-4"
                      >
                        {isLinking ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Vinculando...
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4 mr-2" />
                            Vincular PDF
                          </>
                        )}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
                
                <div>
                  <h2 className="text-lg font-bold text-slate-900 mb-6">Contact Information</h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Contact</h3>
                      
                      {/* Email - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700">
                        <Mail className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <p className="break-all font-medium">{lead.email || ""}</p>
                        </div>
                      </div>

                      {/* Phone - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700">
                        {lead.phone ? (
                          <button
                            onClick={handleCallLead}
                            disabled={isInitiatingCall || !isReady}
                            className="p-1.5 rounded-lg hover:bg-blue-100 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed relative"
                            title={isInitiatingCall ? "Iniciando chamada..." : "Ligar para este número"}
                          >
                            {isInitiatingCall ? (
                              <div className="w-5 h-5 flex items-center justify-center">
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              </div>
                            ) : (
                              <Phone className="w-5 h-5 text-slate-400 group-hover:text-blue-600 flex-shrink-0" />
                            )}
                          </button>
                        ) : (
                          <Phone className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className="text-xs text-slate-500">Phone</p>
                          <p className="font-medium">
                            {lead.phone ? formatPhoneNumber(lead.phone) : ""}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Personal</h3>
                      
                      {/* Date of Birth - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700">
                        <Calendar className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Date of Birth</p>
                          <p className="font-medium">
                            {lead.date_of_birth 
                              ? safeFormat(new Date(lead.date_of_birth), "MMMM d, yyyy")
                              : ""}
                          </p>
                        </div>
                      </div>

                      {/* Gender - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700">
                        <Users className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Gender</p>
                          <p className="font-medium capitalize">{lead.gender || ""}</p>
                        </div>
                      </div>

                      {/* Language - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700">
                        <Languages className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Language</p>
                          <p className="font-medium capitalize">{lead.language || ""}</p>
                        </div>
                      </div>

                      {/* Preferred Name - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700">
                        <UserCog className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Preferred Name</p>
                          <p className="font-medium">{lead.preferred_name || ""}</p>
                        </div>
                      </div>

                      {/* Country of Birth - sempre mostrar */}
                      <div className="flex items-start gap-3 text-slate-700">
                        <Globe className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Country of Birth</p>
                          <p className="font-medium">{lead.country_of_birth || ""}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address - sempre mostrar */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Address</h3>
                  <div className="flex items-start gap-3 text-slate-700">
                    <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      {lead.address?.street ? (
                        <>
                          <p className="font-medium">
                            {lead.address.street}
                            {lead.address.line2 && `, ${lead.address.line2}`}
                          </p>
                          <p className="text-sm text-slate-600">
                            {[
                              lead.address.city, 
                              getStateName(lead.address.state, lead.address.country), 
                              lead.address.zip, 
                              lead.address.country
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        </>
                      ) : (
                        <p className="font-medium"></p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="p-6 bg-gradient-to-br from-muted/50 to-muted rounded-xl border">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4">Quick Overview</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-slate-500">Status</p>
                      <Badge className={`${statusColors[lead.status]} border font-medium mt-1 pointer-events-none`}>
                        {lead.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Source</p>
                      <Badge className={`${sourceColors[lead.source] || 'bg-slate-100 text-slate-700'} border-0 font-medium mt-1 pointer-events-none`}>
                        {lead.source.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-2">Lead Owner</p>
                      <div className="flex items-center gap-2">
                        <p className="text-slate-900 font-medium flex-1">
                          {assignedUser 
                            ? (assignedUser.first_name && assignedUser.last_name
                                ? `${assignedUser.first_name} ${assignedUser.last_name}`
                                : assignedUser.full_name)
                            : "Unassigned"}
                        </p>
                        <button
                          onClick={() => setShowAssignDialog(true)}
                          className="p-1.5 hover:bg-white rounded-lg transition-colors"
                          title="Change assignment"
                        >
                          <UserCog className="w-4 h-4 text-blue-600" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Tasks</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{lead.tasks?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Calls</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{lead.calls?.length || 0}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <Button
                      onClick={() => setActiveTab("tasks")}
                      variant="outline"
                      className="w-full justify-start border-slate-300"
                    >
                      View Tasks
                    </Button>
                    <Button
                      onClick={() => setActiveTab("calls")}
                      variant="outline"
                      className="w-full justify-start border-slate-300"
                    >
                      View Calls
                    </Button>
                    <Button
                      onClick={() => setActiveTab("whatsapp")}
                      variant="outline"
                      className="w-full justify-start border-slate-300"
                    >
                      View Messages
                    </Button>
                    {['qualified', 'not_qualified'].includes(lead.status) ? (
                      <Button
                        onClick={handleCloneLead}
                        disabled={isProcessing}
                        variant="outline"
                        className="w-full justify-start border-slate-300"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Clone Lead
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setIsEditing(true)}
                        variant="outline"
                        className="w-full justify-start border-slate-300"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Lead
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "tasks" && (
            <TasksTab 
              tasks={lead.tasks || []} 
              onUpdate={handleUpdateTasks}
              userEmail={user?.email}
              isProcessing={isProcessing}
            />
          )}

          {activeTab === "calls" && (
            <CallsTab 
              leadId={lead.id}
              contactId={lead.contact_id || null}
            />
          )}

          {activeTab === "meetings" && (
            <MeetingsTab 
              leadId={lead.id}
              contactId={lead.contact_id || null}
            />
          )}

          {activeTab === "whatsapp" && (
            <ChatCore
              contactId={lead.contact_id || lead.id}
              leadId={lead.id}
              leadPhone={lead.phone}
              showConversationSelector
              header={
                <ContactChatHeader
                  phoneNumber={lead.phone}
                  isConnected
                />
              }
            />
          )}

          {activeTab === "documents" && (
            <div className="space-y-6">
              {/* Alerta de PDF não vinculado */}
              {hasPdf && !pdfLinked && !pdfLoading && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="flex items-center justify-between">
                    <span className="text-amber-800">
                      Este lead possui um PDF de avaliação que não está vinculado aos documentos.
                    </span>
                    <Button
                      onClick={async () => {
                        try {
                          await linkPdf();
                          toast.success('PDF vinculado com sucesso!');
                          await refreshPdf();
                        } catch (error) {
                          toast.error('Erro ao vincular PDF');
                          console.error(error);
                        }
                      }}
                      disabled={isLinking}
                      size="sm"
                      className="ml-4"
                    >
                      {isLinking ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Vinculando...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Vincular PDF
                        </>
                      )}
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
              <DocumentsTab leadId={lead.id} />
            </div>
          )}

          {activeTab === "notes" && (
            <NotesTab leadId={lead.id} />
          )}
        </div>

        {/* Assignment Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Lead Owner</DialogTitle>
              <DialogDescription>
                Select a sales representative to assign this lead to
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {isLoadingSalesUsers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : salesUsers.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No sales representatives found. Please configure roles in Settings.
                  </AlertDescription>
                </Alert>
              ) : (
                <Select
                  value={lead.assigned_to || "unassigned"}
                  onValueChange={async (value) => {
                    setIsProcessing(true);
                    try {
                      const { error } = await supabase
                        .from('leads')
                        .update({ 
                          assigned_to: value === "unassigned" ? null : value,
                          updated_at: new Date().toISOString()
                        })
                        .eq('id', lead.id);
                      
                      if (error) throw error;
                      
                      await loadLeadData();
                      setShowAssignDialog(false);
                      toast.success('Lead assigned successfully');
                    } catch (error) {
                      console.error("Error updating assigned user:", error);
                      toast.error('Error assigning lead');
                    }
                    setIsProcessing(false);
                  }}
                  disabled={isProcessing}
                >
                  <SelectTrigger className="border-slate-300">
                    <SelectValue placeholder="Select lead owner">
                      {assignedUser 
                          ? (assignedUser.first_name && assignedUser.last_name
                              ? `${assignedUser.first_name} ${assignedUser.last_name}`
                              : assignedUser.display_name || assignedUser.email)
                          : "Unassigned"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {[...salesUsers]
                      .sort((a, b) => {
                        const nameA = (a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : a.display_name || a.email || '').toLowerCase();
                        const nameB = (b.first_name && b.last_name ? `${b.first_name} ${b.last_name}` : b.display_name || b.email || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((sdr) => (
                        <SelectItem key={sdr.id} value={sdr.id}>
                          {sdr.first_name && sdr.last_name 
                            ? `${sdr.first_name} ${sdr.last_name}`
                            : sdr.display_name || sdr.email}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Convert Confirmation Dialog */}
        <AlertDialog open={showConvertDialog} onOpenChange={(open) => {
          setShowConvertDialog(open);
          if (!open) setSelectedUserId(null);
        }}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>Convert Lead to Deal</AlertDialogTitle>
              <AlertDialogDescription>
                Select the user who will be responsible for this deal.
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="user-select">Deal Owner *</Label>
                <Select 
                  value={selectedUserId || ''} 
                  onValueChange={setSelectedUserId}
                  disabled={usersLoading}
                >
                  <SelectTrigger id="user-select">
                    <SelectValue placeholder="Select a user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users
                      .filter(u => u.active)
                      .map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim()}
                          {user.department?.name && (
                            <span className="text-xs text-muted-foreground ml-2">
                              ({user.department.name})
                            </span>
                          )}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <p className="text-sm text-muted-foreground">
                This action will:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground ml-2 space-y-1">
                <li>Create a new Contact</li>
                <li>Create a new Deal assigned to the selected user</li>
                <li>Mark this Lead as "Qualified"</li>
              </ul>
            </div>
            
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setSelectedUserId(null)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConvertLead}
                disabled={isProcessing || !selectedUserId || usersLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Converting...
                  </>
                ) : (
                  'Convert to Deal'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Not Convert Confirmation Dialog */}
        <AlertDialog open={showNotConvertDialog} onOpenChange={(open) => {
          setShowNotConvertDialog(open);
          if (!open) setNotConvertedReason('');
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark as Not Converted</AlertDialogTitle>
              <AlertDialogDescription>
                Please select the reason why this lead will not be converted:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="not-converted-reason" className="text-sm font-medium mb-2 block">
                Reason
              </Label>
              <Select value={notConvertedReason} onValueChange={setNotConvertedReason}>
                <SelectTrigger id="not-converted-reason" className="border-slate-300">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {notConvertedReasons.map((reason) => (
                    <SelectItem key={reason} value={reason}>
                      {reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setNotConvertedReason('')}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleNotConvertLead}
                disabled={isProcessing}
                className="bg-red-600 hover:bg-red-700"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Confirm'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
